import { forwardRef, useCallback } from "react";

import { cn } from "@/shared/lib/utils";

import { useItemMoveDraggable } from "../hooks/use-item-move-draggable";
import type { Item } from "../types";

interface ItemMoveDraggableProps extends React.ComponentProps<"div"> {
  item: Item;
  items: Item[];
  titles: string[];
  disabled?: boolean;
  onMoved?: () => void;
}

export const ItemMoveDraggable = forwardRef<
  HTMLDivElement,
  ItemMoveDraggableProps
>(function ItemMoveDraggable(
  {
    item,
    items,
    titles,
    disabled = false,
    onMoved,
    className,
    children,
    ...props
  },
  forwardedRef,
) {
  const { ref: draggableRef, isDragging } = useItemMoveDraggable({
    item,
    items,
    titles,
    disabled,
    onMoved,
  });
  const setRef = useCallback(
    (element: HTMLDivElement | null) => {
      draggableRef(element);

      if (typeof forwardedRef === "function") {
        forwardedRef(element);
      } else if (forwardedRef) {
        forwardedRef.current = element;
      }
    },
    [draggableRef, forwardedRef],
  );

  return (
    <div
      {...props}
      ref={setRef}
      className={cn(
        !disabled &&
          "cursor-grab transition-[opacity,filter] duration-150 active:cursor-grabbing",
        isDragging && "opacity-50 grayscale-[0.35]",
        className,
      )}
    >
      {children}
    </div>
  );
});
